import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, Profile, FavoriteCharacter, AnimeCharacter } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AvatarEditor from './components/AvatarEditor';
import SEO from '../../components/SEO';
import { toast } from 'sonner';

type RatedCharacter = {
  character_id: string;
  rating: number;
  character_name: string;
  image_url: string;
  anime_name: string;
};

type FollowUser = {
  id: string;
  username: string;
  avatar_url: string | null;
  ratings_count: number;
};

export default function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [favorites, setFavorites] = useState<(FavoriteCharacter & { anime_characters: AnimeCharacter })[]>([]);
  const [highestRated, setHighestRated] = useState<RatedCharacter[]>([]);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [showUsernameEditor, setShowUsernameEditor] = useState(false);
  const [showBioEditor, setShowBioEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowList, setShowFollowList] = useState<'followers' | 'following' | null>(null);
  const isOwnProfile = user?.id === id;

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    if (id) {
      loadProfile();
      loadFavorites();
      loadHighestRated();
      loadFollowStats();
      if (user && !isOwnProfile) {
        checkIfFollowing();
      }
    }
  }, [id, user]);

  const loadProfile = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (data) {
      setProfile(data);
    }
    setLoading(false);
  };

  const loadFavorites = async () => {
    const { data } = await supabase
      .from('favorite_characters')
      .select(`
        id,
        character_id,
        position,
        anime_characters (
          character_name,
          image_url
        )
      `)
      .eq('user_id', id)
      .order('position', { ascending: true })
      .limit(5);

    if (data) {
      setFavorites(data as (FavoriteCharacter & { anime_characters: AnimeCharacter })[]);
    }
  };

  const loadHighestRated = async () => {
    const { data } = await supabase
      .from('ratings')
      .select(`
        character_id,
        rating,
        anime_characters (
          character_name,
          image_url,
          anime_name
        )
      `)
      .eq('user_id', id)
      .order('rating', { ascending: false })
      .limit(10);

    if (data) {
      const formattedData: RatedCharacter[] = data
        .filter(item => item.anime_characters)
        .map(item => ({
          character_id: item.character_id,
          rating: item.rating,
          character_name: (item.anime_characters as any).character_name,
          image_url: (item.anime_characters as any).image_url,
          anime_name: (item.anime_characters as any).anime_name,
        }));
      
      setHighestRated(formattedData);
    }
  };

  const loadFollowStats = async () => {
    // Get followers count
    const { count: followers } = await supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('following_id', id);

    // Get following count
    const { count: following } = await supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('follower_id', id);

    setFollowersCount(followers || 0);
    setFollowingCount(following || 0);
  };

  const checkIfFollowing = async () => {
    if (!user || !id) return;

    const { data } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', user.id)
      .eq('following_id', id)
      .maybeSingle();

    setIsFollowing(!!data);
  };

  const handleFollowToggle = async () => {
    if (!user) {
      toast.error('Please sign in to follow users');
      return;
    }

    if (isOwnProfile) return;

    setFollowLoading(true);

    try {
      if (isFollowing) {
        // Unfollow
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', id);

        if (error) throw error;

        setIsFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
        toast.success('Unfollowed successfully');
      } else {
        // Follow
        const { error } = await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: id
          });

        if (error) throw error;

        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
        toast.success('Following successfully');
      }
    } catch (error: any) {
      console.error('Error toggling follow:', error);
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const toggleHighestRatedVisibility = async () => {
    if (!user || !profile) return;

    const newVisibility = !profile.show_highest_rated;

    const { error } = await supabase
      .from('profiles')
      .update({ show_highest_rated: newVisibility })
      .eq('id', user.id);

    if (!error) {
      setProfile({ ...profile, show_highest_rated: newVisibility });
      toast.success(newVisibility ? 'Highest rated characters are now visible' : 'Highest rated characters are now hidden');
    } else {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const getAvatarStyle = () => {
    if (!profile?.avatar_zoom) {
      return {
        objectFit: 'cover' as const,
        objectPosition: 'top center',
      };
    }

    const zoom = profile.avatar_zoom;
    const containerSize = 128;
    
    const cropWidth = zoom.width;
    const cropHeight = zoom.height;
    
    const scale = containerSize / cropWidth;
    
    const offsetX = -zoom.x * scale;
    const offsetY = -zoom.y * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
    };
  };

  const jsonLd = profile ? {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: profile.username,
      image: profile.avatar_url || undefined,
      interactionStatistic: {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/ReviewAction',
        userInteractionCount: profile.ratings_count
      }
    }
  } : undefined;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
        <Navbar />
        <div className="pt-24 pb-16 px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Profile not found</h1>
          <Link
            to="/"
            className="inline-block px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const shouldShowHighestRated = profile.show_highest_rated || isOwnProfile;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title={`${profile.username}'s Profile - Anime HOT or NOT`}
        description={`View ${profile.username}'s anime character ratings and favorite characters. ${profile.ratings_count} ratings submitted. Join the anime rating community!`}
        keywords={`${profile.username}, anime profile, anime ratings, favorite anime characters, anime community member`}
        canonical={`${siteUrl}/profile/${id}`}
        ogType="profile"
        ogImage={profile.avatar_url}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative">
                {profile.avatar_url ? (
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-pink-500 bg-gray-100">
                    <img
                      src={profile.avatar_url}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                      style={getAvatarStyle()}
                      onError={(e) => {
                        console.error('Avatar image failed to load:', profile.avatar_url);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-32 h-32 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full text-white text-5xl font-bold">
                    {profile.username.charAt(0).toUpperCase()}
                  </div>
                )}
                {isOwnProfile && (
                  <button
                    onClick={() => setShowAvatarEditor(true)}
                    className="absolute bottom-0 right-0 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-lg border-2 border-pink-500 cursor-pointer hover:bg-pink-50 transition-colors"
                  >
                    <i className="ri-edit-line text-pink-500"></i>
                  </button>
                )}
              </div>

              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {profile.username}
                  </h1>
                  {isOwnProfile && (
                    <button
                      onClick={() => setShowUsernameEditor(true)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-pink-500 transition-colors cursor-pointer"
                      title="Edit username"
                    >
                      <i className="ri-edit-line text-lg"></i>
                    </button>
                  )}
                </div>
                
                <div className="flex items-center justify-center md:justify-start gap-6 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-heart-fill text-pink-500"></i>
                    <span>{profile.ratings_count} ratings</span>
                  </div>
                  <button
                    onClick={() => setShowFollowList('followers')}
                    className="flex items-center gap-2 hover:text-pink-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-user-follow-line text-pink-500"></i>
                    <span>{followersCount} followers</span>
                  </button>
                  <button
                    onClick={() => setShowFollowList('following')}
                    className="flex items-center gap-2 hover:text-pink-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-user-add-line text-pink-500"></i>
                    <span>{followingCount} following</span>
                  </button>
                </div>

                {/* Follow Button for other users' profiles */}
                {!isOwnProfile && user && (
                  <button
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    className={`px-6 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      isFollowing
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-lg'
                    } disabled:opacity-50`}
                  >
                    {followLoading ? (
                      <span className="flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Loading...
                      </span>
                    ) : isFollowing ? (
                      <span className="flex items-center gap-2">
                        <i className="ri-user-unfollow-line"></i>
                        Unfollow
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <i className="ri-user-add-line"></i>
                        Follow
                      </span>
                    )}
                  </button>
                )}

                {/* Bio Section */}
                <div className="mt-4">
                  {profile.bio ? (
                    <div className="relative">
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {profile.bio}
                      </p>
                      {isOwnProfile && (
                        <button
                          onClick={() => setShowBioEditor(true)}
                          className="absolute -top-1 -right-1 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-pink-500 transition-colors cursor-pointer"
                          title="Edit bio"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  ) : isOwnProfile ? (
                    <button
                      onClick={() => setShowBioEditor(true)}
                      className="text-sm text-pink-500 hover:text-pink-600 font-medium cursor-pointer whitespace-nowrap"
                    >
                      + Add bio
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Highest Rated Characters Section */}
          {shouldShowHighestRated && highestRated.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Highest Rated Characters</h2>
                {isOwnProfile && (
                  <button
                    onClick={toggleHighestRatedVisibility}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${profile.show_highest_rated ? 'ri-eye-line' : 'ri-eye-off-line'}`}></i>
                    {profile.show_highest_rated ? 'Visible' : 'Hidden'}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {highestRated.map((rated, index) => (
                  <Link
                    key={rated.character_id}
                    to={`/character/${rated.character_id}`}
                    className="group"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden shadow-md group-hover:shadow-xl transition-shadow">
                      <img
                        src={rated.image_url}
                        alt={rated.character_name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <div className="absolute top-2 left-2 w-8 h-8 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full text-white font-bold text-sm shadow-lg">
                        {rated.rating}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform">
                        <p className="text-xs font-semibold truncate">{rated.character_name}</p>
                        <p className="text-xs opacity-80 truncate">{rated.anime_name}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {!shouldShowHighestRated && !isOwnProfile && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="text-center py-8">
                <i className="ri-eye-off-line text-5xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-500">
                  This user has hidden their highest rated characters
                </p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Top 5 Favorite Characters</h2>
              {isOwnProfile && (
                <Link
                  to="/favorites"
                  className="text-sm font-medium text-pink-500 hover:text-pink-600 whitespace-nowrap"
                >
                  Manage Favorites
                </Link>
              )}
            </div>

            {favorites.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {favorites.map((fav, index) => (
                  <Link
                    key={fav.id}
                    to={`/character/${fav.character_id}`}
                    className="group"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden shadow-md group-hover:shadow-xl transition-shadow">
                      <img
                        src={fav.anime_characters.image_url}
                        alt={fav.anime_characters.character_name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute top-2 left-2 w-8 h-8 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full text-white font-bold text-sm">
                        {index + 1}
                      </div>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-gray-900 text-center">
                      {fav.anime_characters.character_name}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="ri-heart-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-sm text-gray-500">
                  {isOwnProfile
                    ? 'You haven\'t added any favorite characters yet'
                    : 'No favorite characters added'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {showAvatarEditor && (
        <AvatarEditor
          onClose={() => setShowAvatarEditor(false)}
          onSave={loadProfile}
        />
      )}

      {showUsernameEditor && (
        <UsernameEditor
          currentUsername={profile.username}
          onClose={() => setShowUsernameEditor(false)}
          onSave={loadProfile}
        />
      )}

      {showBioEditor && (
        <BioEditor
          currentBio={profile.bio || ''}
          onClose={() => setShowBioEditor(false)}
          onSave={loadProfile}
        />
      )}

      {showFollowList && (
        <FollowListModal
          userId={id!}
          type={showFollowList}
          currentUserId={user?.id}
          onClose={() => setShowFollowList(null)}
          onFollowChange={() => {
            loadFollowStats();
            if (user && !isOwnProfile) {
              checkIfFollowing();
            }
          }}
        />
      )}
    </div>
  );
}

// Username Editor Component
function UsernameEditor({ 
  currentUsername, 
  onClose, 
  onSave 
}: { 
  currentUsername: string;
  onClose: () => void;
  onSave: () => void;
}) {
  const { user } = useAuth();
  const [username, setUsername] = useState(currentUsername);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    const trimmedUsername = username.trim();
    
    if (trimmedUsername.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    
    if (trimmedUsername.length > 20) {
      setError('Username must be less than 20 characters');
      return;
    }
    
    if (!/^[a-zA-Z0-9_]+$/.test(trimmedUsername)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }
    
    if (trimmedUsername === currentUsername) {
      onClose();
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Check if username is already taken
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', trimmedUsername)
        .single();

      if (existingUser) {
        if (isMountedRef.current) {
          setError('Username is already taken');
          setLoading(false);
        }
        return;
      }

      // Update username
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ username: trimmedUsername })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Show success message
      toast.success('Username updated successfully!');
      
      if (isMountedRef.current) {
        onSave();
        onClose();
      }
    } catch (err: any) {
      console.error('Error updating username:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Failed to update username');
        setLoading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-900">
          Edit Username
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
              placeholder="Enter new username"
              autoFocus
            />
            <p className="mt-2 text-xs text-gray-500">
              3-20 characters, letters, numbers, and underscores only
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-all whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all disabled:opacity-50 whitespace-nowrap"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Bio Editor Component
function BioEditor({ 
  currentBio, 
  onClose, 
  onSave 
}: { 
  currentBio: string;
  onClose: () => void;
  onSave: () => void;
}) {
  const { user } = useAuth();
  const [bio, setBio] = useState(currentBio);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isMountedRef = useRef(true);
  const MAX_BIO_LENGTH = 500;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const sanitizeText = (text: string): string => {
    // Remove any HTML tags and script content
    return text
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]+>/g, '')
      .trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    const sanitizedBio = sanitizeText(bio);
    
    if (sanitizedBio.length > MAX_BIO_LENGTH) {
      setError(`Bio must be ${MAX_BIO_LENGTH} characters or less`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ bio: sanitizedBio || null })
        .eq('id', user.id);

      if (updateError) throw updateError;

      toast.success('Bio updated successfully!');
      
      if (isMountedRef.current) {
        onSave();
        onClose();
      }
    } catch (err: any) {
      console.error('Error updating bio:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Failed to update bio');
        setLoading(false);
      }
    }
  };

  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newBio = e.target.value;
    setBio(newBio);
    setError('');
  };

  const remainingChars = MAX_BIO_LENGTH - bio.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-900">
          Edit Bio
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              About You
            </label>
            <textarea
              value={bio}
              onChange={handleBioChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm resize-none"
              placeholder="Tell others about yourself..."
              rows={6}
              autoFocus
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">
                Plain text only (no HTML)
              </p>
              <p className={`text-xs font-medium ${remainingChars < 50 ? 'text-red-500' : 'text-gray-500'}`}>
                {remainingChars} characters left
              </p>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-all whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || bio.length > MAX_BIO_LENGTH}
              className="flex-1 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all disabled:opacity-50 whitespace-nowrap"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Follow List Modal Component
function FollowListModal({
  userId,
  type,
  currentUserId,
  onClose,
  onFollowChange,
}: {
  userId: string;
  type: 'followers' | 'following';
  currentUserId?: string;
  onClose: () => void;
  onFollowChange: () => void;
}) {
  const [users, setUsers] = useState<FollowUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    if (currentUserId) {
      loadCurrentUserFollowing();
    }
  }, [userId, type, currentUserId]);

  const loadUsers = async () => {
    setLoading(true);
    
    if (type === 'followers') {
      // Get users who follow this profile
      const { data, error } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', userId);

      if (error) {
        console.error('Error loading followers:', error);
        setLoading(false);
        return;
      }

      if (data && data.length > 0) {
        const followerIds = data.map(f => f.follower_id);
        
        // Get profile details for all followers
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, username, avatar_url, avatar_zoom, ratings_count')
          .in('id', followerIds);

        if (profilesData) {
          setUsers(profilesData);
        }
      }
    } else {
      // Get users this profile follows
      const { data, error } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', userId);

      if (error) {
        console.error('Error loading following:', error);
        setLoading(false);
        return;
      }

      if (data && data.length > 0) {
        const followingIds = data.map(f => f.following_id);
        
        // Get profile details for all following
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, username, avatar_url, avatar_zoom, ratings_count')
          .in('id', followingIds);

        if (profilesData) {
          setUsers(profilesData);
        }
      }
    }
    
    setLoading(false);
  };

  const loadCurrentUserFollowing = async () => {
    if (!currentUserId) return;

    const { data } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', currentUserId);

    if (data) {
      setFollowingIds(new Set(data.map(f => f.following_id)));
    }
  };

  const handleFollowToggle = async (targetUserId: string) => {
    if (!currentUserId) {
      toast.error('Please sign in to follow users');
      return;
    }

    if (targetUserId === currentUserId) return;

    setActionLoading(targetUserId);

    try {
      const isCurrentlyFollowing = followingIds.has(targetUserId);

      if (isCurrentlyFollowing) {
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', targetUserId);

        if (error) throw error;

        setFollowingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
        toast.success('Unfollowed successfully');
      } else {
        const { error } = await supabase
          .from('follows')
          .insert({
            follower_id: currentUserId,
            following_id: targetUserId
          });

        if (error) throw error;

        setFollowingIds(prev => new Set(prev).add(targetUserId));
        toast.success('Following successfully');
      }

      onFollowChange();
    } catch (error: any) {
      console.error('Error toggling follow:', error);
      toast.error('Failed to update follow status');
    } finally {
      setActionLoading(null);
    }
  };

  const getAvatarStyle = (avatarZoom: any) => {
    if (!avatarZoom) {
      return {
        objectFit: 'cover' as const,
        objectPosition: 'top center',
      };
    }

    const zoom = avatarZoom;
    const containerSize = 48; // Avatar size in the list
    
    const cropWidth = zoom.width;
    const cropHeight = zoom.height;
    
    const scale = containerSize / cropWidth;
    
    const offsetX = -zoom.x * scale;
    const offsetY = -zoom.y * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
    };
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex-shrink-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 capitalize">
            {type}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-pink-500 border-t-transparent"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-user-line text-5xl text-gray-300 mb-3"></i>
              <p className="text-sm text-gray-500">
                {type === 'followers' ? 'No followers yet' : 'Not following anyone'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {users.map((followUser) => (
                <div
                  key={followUser.id}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                >
                  <Link
                    to={`/profile/${followUser.id}`}
                    onClick={onClose}
                    className="flex-shrink-0"
                  >
                    {followUser.avatar_url ? (
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-pink-200 bg-gray-100">
                        <img
                          src={followUser.avatar_url}
                          alt={followUser.username}
                          className="w-full h-full object-cover"
                          style={getAvatarStyle((followUser as any).avatar_zoom)}
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full text-white text-lg font-bold">
                        {followUser.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Link>

                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/profile/${followUser.id}`}
                      onClick={onClose}
                      className="block"
                    >
                      <p className="text-sm font-semibold text-gray-900 truncate hover:text-pink-500 transition-colors">
                        {followUser.username}
                      </p>
                      <p className="text-xs text-gray-500">
                        {followUser.ratings_count} ratings
                      </p>
                    </Link>
                  </div>

                  {currentUserId && followUser.id !== currentUserId && (
                    <button
                      onClick={() => handleFollowToggle(followUser.id)}
                      disabled={actionLoading === followUser.id}
                      className={`flex-shrink-0 px-4 py-1.5 text-xs font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                        followingIds.has(followUser.id)
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-lg'
                      } disabled:opacity-50`}
                    >
                      {actionLoading === followUser.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : followingIds.has(followUser.id) ? (
                        'Unfollow'
                      ) : (
                        'Follow'
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
